import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EndpointDictionary } from 'src/app/utils/endpoints-dictionary';
import { environment } from 'src/environments/environment';
import {
  HttpService,
  LocalStorageService,
  MessageService,
  SessionService,
} from 'brainloper-ui';
import { HttpClient } from '@angular/common/http';
import { UserAuth } from 'src/app/models/auth/user-auth';
import { Plan } from 'src/app/models/configuration/plan';
import { Module } from 'src/app/models/administration/module';
import { Configuration } from 'src/app/models/auth/configuration';
import { Company } from 'src/app/models/administration/company';
import { BaseResponse } from 'src/app/models/commos/base-response';
import { RefreshToken } from 'src/app/models/administration/refresh-token';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(
    private message: MessageService,
    private http: HttpService,
    private session: SessionService,
    private router: Router,
    private storage: LocalStorageService,
    private httpClient: HttpClient
  ) {}

  private isLoggingOut = false;

  public async getUserProfile(user: UserAuth): Promise<Boolean> {
    const companyIdActive = await this.http.getDataBody<boolean>(
      EndpointDictionary.Login.COMPANY_IS_ACTIVE
    );
    if (!companyIdActive) {
      this.Logout(true);
      return false;
    }
    this.login(user, EndpointDictionary.Login.GET_USER_PROFILE, false);
    return true;
  }

  public async login(
    user: UserAuth,
    url: string = EndpointDictionary.Login.LOGIN_DATA,
    navigateHome: boolean = true
  ): Promise<void> {
    this.isLoggingOut = false;
    try {
      let res: BaseResponse<Configuration> = await this.http.postDataPromise(
        url,
        user
      );
      if (res['code'] === 0) {
        this.session.setData('cargando', true);
        this.storage.set<Boolean>('initSession', true);
        if (res.body.token) this.storage.set<string>('token', res.body.token);
        if (res.body.refreshToken)
          this.storage.set<number>('refreshToken', res.body.refreshToken);
        this.storage.set<string>('rol', res.body.user.role);
        this.storage.set<number>('roleId', res.body.user.roleId);
        this.storage.set<string>('companyName', res.body.company.name);
        this.storage.set<string>('userName', res.body.user.name.split(' ')[0]);
        this.storage.set<number>('cedula', res.body.user.userId);
        this.storage.set<number>('userId', res.body.user.userId);
        this.storage.set<string>('user', res.body.user.user);
        this.storage.set<Company>('company', res.body.company);
        this.storage.set<number>('companyId', res.body.company.id);
        this.storage.set<Plan>('plan', res.body.plan);
        this.storage.set<Module[]>('modules', res.body.modules);
        this.storage.set<boolean>(
          'inventory_module',
          res.body.haveModuleInventory
        );
        this.storage.set<boolean>(
          'missingPaymentAlert',
          res.body.company.paymentAlert
        );
        this.session.setData('date', new Date());

        this.message.SuccessToast(res['message']);
        if (navigateHome) {
          // Verificar si hay una URL pendiente para redirigir tras el login
          const redirectUrl = this.storage.get<string>('postLoginRedirect');
          if (redirectUrl) {
            // Consumirla para que no se reutilice
            this.storage.remove('postLoginRedirect');
            // Si es una URL absoluta (ej: http://localhost:3000?eventId=510) usar window.location
            if (/^(http|https):\/\//i.test(redirectUrl)) {
              window.location.href = redirectUrl;
            } else {
              // Navegación interna de Angular
              this.router.navigateByUrl(redirectUrl);
            }
          } else {
            this.router.navigate(['inicio']);
          }
        }
        this.message.closeLoading();
      } else {
        this.message.Error(
          'Error de acceso',
          `Código: ${res['code']} - ${res['message']}`
        );
        this.message.closeLoading();
      }
    } catch (error) {
      this.message.Error('Error de acceso', '');
      this.Logout();
    }
  }

  private cleanClientSession(pendingRedirect: string | null, isCompanyInactive: boolean = false) {
    const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
    const hostname = window.location.hostname;
    // Derive parent domain (e.g. sub.example.com → .example.com) for cross-subdomain cookies
    const domainParts = hostname.split('.');
    const parentDomain = domainParts.length > 1
      ? '.' + domainParts.slice(-2).join('.')
      : hostname;

    document.cookie.split(';').forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = (eqPos > -1 ? cookie.substr(0, eqPos) : cookie).trim();
      if (!name) return;
      // Attempt removal on all common path/domain combinations
      const paths = ['/', '/app', window.location.pathname];
      const domains = [hostname, parentDomain, ''];
      paths.forEach(path => {
        domains.forEach(domain => {
          const domainAttr = domain ? `;domain=${domain}` : '';
          document.cookie = `${name}=;${expiry};path=${path}${domainAttr}`;
        });
      });
    });

    // Limpiar localStorage
    const storedSideBarState = this.storage.get<boolean>('sideBarExtended');
    this.storage.clear();
    sessionStorage.clear();
    this.storage.set('sideBarExtended', storedSideBarState);

    this.router.navigate(['/']);
    this.message.closeLoading();

    if (isCompanyInactive) {
      this.message.Error('Error de acceso', 'Compañía inactiva');
    } else {
      this.message.Info('', 'Sesión finalizada');
    }

    this.storage.set<boolean>('initSession', false);
    this.storage.set<Date>('date', new Date());

    if (pendingRedirect) {
      this.storage.set<string>('postLoginRedirect', pendingRedirect);
    }
    this.isLoggingOut = false;
  }

  public async Logout(isCompanyInactive: boolean = false) {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    const pendingRedirect = this.storage.get<string>('postLoginRedirect');

    try {
      if(this.storage.get<string>('token')){
        await this.http.postDataPromise(EndpointDictionary.Login.LOGOUT, {}, null, true);
        this.storage.remove('token');
      }

    } catch (error) {
      console.error('Logout request failed, cleaning session anyway.', error);
    } finally {
      this.cleanClientSession(pendingRedirect, isCompanyInactive);
    }
  }

  public RefreshToken(token: string) {
    return this.httpClient.post<BaseResponse<RefreshToken>>(
      EndpointDictionary.Security.REFRESH_TOKEN,
      token
    );
  }
}
